import React, { useMemo, useState } from 'react';
import propTypes from 'prop-types';
import Button from 'tailwind-components/Button/Button';
import FeaturedCarrierAgentsModal from './FeaturedCarrierAgentsModal';
import { DEFAULT_EMPTY_HIGHLIGHT_MESSAGE } from './featuredCarrierMessages';
import { cn } from 'lib/utils';

// Prefix formatted values with a tilde to make it clear the figure is an
// approximation (numbers are rounded to one decimal in the K / M buckets).
function formatCommissions(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  if (Math.abs(num) >= 1_000_000) {
    return `~$${(num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1)}M`;
  }
  if (Math.abs(num) >= 1_000) {
    return `~$${(num / 1_000).toFixed(num % 1_000 === 0 ? 0 : 1)}K`;
  }
  return `~$${num.toLocaleString('en-US')}`;
}

// summary shown under the carrier name, null while the initial fetch is still running
function getSummaryLine({ hasData, agentsMissingCount, emptyMessage }) {
  if (!hasData) return null;
  if (agentsMissingCount > 0) {
    return `${agentsMissingCount} of your agents sell heavily with this carrier but are not appointed yet`;
  }
  return emptyMessage || DEFAULT_EMPTY_HIGHLIGHT_MESSAGE;
}

function FeaturedCarrierCard({
  highlightedCarrier,
  data,
  loading,
  agencyId,
  agencyName,
  mainZohoId,
  className
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const carrierName = useMemo(
    () => data?.carrier || highlightedCarrier?.name || '',
    [data, highlightedCarrier]
  );

  const highlightMessage = useMemo(
    () => (highlightedCarrier?.highlightMessage || '').trim(),
    [highlightedCarrier]
  );

  const emptyHighlightMessage = useMemo(
    () => (highlightedCarrier?.emptyHighlightMessage || '').trim(),
    [highlightedCarrier]
  );

  const agents = useMemo(
    () => (Array.isArray(data?.agents) ? data.agents : []),
    [data]
  );
  const agentsMissingCount = agents.length;

  const totalCommissionsFormatted = useMemo(
    () => formatCommissions(data?.totalCommissions),
    [data]
  );

  if (!highlightedCarrier || !carrierName) {
    return null;
  }

  const hasData = !!data;
  const totalCommissions = Number(data?.totalCommissions);
  const hasCommissions =
    Number.isFinite(totalCommissions) && totalCommissions !== 0;
  const showCommissionsBox = !loading && hasData && hasCommissions;
  const showDetailsButton = !loading && hasData && agentsMissingCount > 0;
  const showRightColumn = showCommissionsBox || showDetailsButton;

  // single-line summary that replaces the per-agent list, hidden during the
  // initial fetch (when we don't yet know if anyone is missing)
  const summaryLine = getSummaryLine({
    hasData,
    agentsMissingCount,
    emptyMessage: emptyHighlightMessage
  });

  return (
    <>
      <article
        className={cn(
          'relative flex w-full min-w-0 flex-col gap-4 overflow-hidden rounded-[10px] px-5 py-5 text-white shadow-[0_2px_6px_rgba(0,0,0,0.08)] sm:flex-row sm:items-center sm:justify-between sm:gap-6 sm:px-7',
          'bg-[linear-gradient(135deg,#0d3b7a,#1a9bdc)]',
          className
        )}
      >
        <div className='flex min-w-0 flex-1 flex-col gap-2'>
          <span className='text-[10px] font-semibold tracking-[0.12em] text-white/70 uppercase'>
            Featured Carrier · Promoted this Month
          </span>

          {highlightMessage ? (
            <h3 className='m-0 text-lg leading-tight font-semibold sm:text-xl'>
              {highlightMessage}
            </h3>
          ) : (
            <h3 className='m-0 text-lg leading-tight font-semibold sm:text-xl'>
              {carrierName}
            </h3>
          )}

          {summaryLine && (
            <p className='m-0 text-sm leading-snug text-white/85'>
              {summaryLine}
            </p>
          )}
        </div>

        {showRightColumn && (
          <div className='flex shrink-0 flex-col items-stretch gap-3'>
            {showCommissionsBox && (
              <div
                className='flex min-w-[140px] flex-col items-center justify-center rounded-lg bg-white/10 px-4 py-3 backdrop-blur-sm'
                title='Approximate value'
              >
                <span className='flex h-6 items-center justify-center text-xl leading-none font-semibold'>
                  {totalCommissionsFormatted}
                </span>
                <span className='mt-1 text-[11px] font-medium text-white/80'>
                  Approx. Total Agency Commissions
                </span>
                {data?.agencyCount > 1 && (
                  <span className='text-[11px] text-white/70'>
                    across {data.agencyCount} agencies
                  </span>
                )}
              </div>
            )}
            {showDetailsButton && (
              <Button
                variant='success'
                size='small'
                rounded='small'
                text='View details'
                onClick={() => setIsModalOpen(true)}
                className='shrink-0'
                fill
              />
            )}
          </div>
        )}
      </article>

      <FeaturedCarrierAgentsModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        carrierName={carrierName}
        carrierId={highlightedCarrier?.id}
        agencyId={agencyId}
        agencyName={agencyName}
        mainZohoId={mainZohoId}
        agents={agents}
        emptyMessage={emptyHighlightMessage}
      />
    </>
  );
}

FeaturedCarrierCard.propTypes = {
  highlightedCarrier: propTypes.object,
  data: propTypes.shape({
    carrier: propTypes.string,
    totalRecords: propTypes.number,
    totalCommissions: propTypes.number,
    agencyCount: propTypes.number,
    agents: propTypes.arrayOf(
      propTypes.shape({
        agentId: propTypes.string,
        agentName: propTypes.string,
        agentEmail: propTypes.string,
        agentNpn: propTypes.string,
        missingStates: propTypes.arrayOf(propTypes.string)
      })
    )
  }),
  loading: propTypes.bool,
  agencyId: propTypes.string,
  agencyName: propTypes.string,
  mainZohoId: propTypes.string,
  className: propTypes.string
};

export default FeaturedCarrierCard;
